import { getToken } from ".";
import { HttpException } from "../../models/exceptions";

// ─── translation-keys API (Compass, one company·key·language record per draft/approved copy) ─

export type TranslationKeyStatus = "draft" | "approved";

function buildTranslationKeysUrl(companyId: string, suffix: string = ""): string {
  return `${(window as any)._env_.REACT_APP_API_HOST}/companies/${companyId}/translation-keys${suffix}`;
}

async function translationKeysAuthHeaders() {
  const token = await getToken();
  return { Authorization: "Bearer " + token, "Content-Type": "application/json" };
}

async function handleTranslationKeysResponse<T>(response: Response, errorMessage: string): Promise<T> {
  if (!response.ok) {
    const error = new HttpException(errorMessage);
    error.code = response.status;
    error.info = await response.json().catch(() => undefined);
    throw error;
  }
  return response.json();
}

export interface TranslationKeyUpsertItem {
  key: string;
  language: string;
  value: string;
}

export interface TranslationKeyPair {
  key: string;
  language: string;
}

/**
 * Bulk fetch translation-key values for a single language, at a given status.
 * Use status="draft" for the editor / draft preview, status="approved" (the default) for
 * whatever should reflect what would actually be sent out.
 */
export async function fetchTranslationKeyValues({
  companyId,
  keys,
  language,
  status = "approved",
  signal,
}: {
  companyId: string;
  keys: string[];
  language: string;
  status?: TranslationKeyStatus;
  signal?: AbortSignal;
}): Promise<Record<string, string>> {
  if (keys.length === 0) return {};

  const response = await fetch(buildTranslationKeysUrl(companyId, ":batchGet"), {
    method: "POST",
    signal,
    headers: await translationKeysAuthHeaders(),
    body: JSON.stringify({ keys, language, status }),
  });

  return handleTranslationKeysResponse(response, "An error occurred while fetching translation values.");
}

/**
 * Bulk save translation-key values as drafts (autosave from the translation editor). Never
 * touches the approved copy -- see publishTranslationKeys.
 */
export async function saveTranslationKeyDrafts({
  companyId,
  translations,
}: {
  companyId: string;
  translations: TranslationKeyUpsertItem[];
}): Promise<{ updated: number }> {
  if (translations.length === 0) return { updated: 0 };

  const response = await fetch(buildTranslationKeysUrl(companyId), {
    method: "PUT",
    headers: await translationKeysAuthHeaders(),
    body: JSON.stringify(translations),
  });

  return handleTranslationKeysResponse(response, "An error occurred while saving translation drafts.");
}

/**
 * Deletes a single (key, language, status) translation-key record. Defaults to the draft copy.
 */
export async function deleteTranslationKey({
  companyId,
  key,
  language,
  status = "draft",
}: {
  companyId: string;
  key: string;
  language: string;
  status?: TranslationKeyStatus;
}): Promise<void> {
  const params = new URLSearchParams({ key, language, status });

  const response = await fetch(`${buildTranslationKeysUrl(companyId)}?${params}`, {
    method: "DELETE",
    headers: await translationKeysAuthHeaders(),
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while deleting the translation draft.");
    error.code = response.status;
    error.info = await response.json().catch(() => undefined);
    throw error;
  }
}

/**
 * Copies each (key, language) pair's draft value into its approved copy. The draft copy is
 * left in place.
 */
export async function publishTranslationKeys({
  companyId,
  pairs,
}: {
  companyId: string;
  pairs: TranslationKeyPair[];
}): Promise<{ published: number }> {
  if (pairs.length === 0) return { published: 0 };

  const response = await fetch(buildTranslationKeysUrl(companyId, ":publish"), {
    method: "POST",
    headers: await translationKeysAuthHeaders(),
    body: JSON.stringify(pairs),
  });

  return handleTranslationKeysResponse(response, "An error occurred while publishing translations.");
}

/**
 * Diffs `keys` against what's actually stored (at `status`) for `language`. Backs the "fully
 * translated" indicator on the language selector.
 */
export async function checkTranslationKeyCompleteness({
  companyId,
  keys,
  language,
  status = "approved",
  signal,
}: {
  companyId: string;
  keys: string[];
  language: string;
  status?: TranslationKeyStatus;
  signal?: AbortSignal;
}): Promise<{ missing: string[]; total: number; translated: number }> {
  if (keys.length === 0) return { missing: [], total: 0, translated: 0 };

  const response = await fetch(buildTranslationKeysUrl(companyId, ":checkCompleteness"), {
    method: "POST",
    signal,
    headers: await translationKeysAuthHeaders(),
    body: JSON.stringify({ keys, language, status }),
  });

  return handleTranslationKeysResponse(response, "An error occurred while checking translation completeness.");
}
